import { useState, type ReactNode } from 'react';
import {
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  ExpandableSection,
  Input,
  Multiselect,
  Select,
  type SelectProps,
  SpaceBetween,
  Textarea,
  TokenGroup,
} from '@amzn/awsui-components-console';
import { ExpressionLanguage, RuleStatus, SecurityConcern } from '@amzn/helianthus-arbiter-client';
import type { RuleEvaluation, RuleInput, ServiceProperties, Severity } from '@amzn/helianthus-arbiter-client';
import { useRules } from '../../hooks/useRules';
import { ruleStatusLabel } from '../../components/RuleStatusIndicator';
import {
  SOURCE_TYPES,
  SOURCE_TYPE_OPTIONS,
  fromRuleInput,
  toRuleInputs,
  type InputFormState,
  type SourceType,
} from './ruleInputs';
import {
  SERVICE_PROPERTIES,
  SERVICE_PROPERTY_KEYS,
  toServiceProperties,
  type ServicePropertyValues,
} from './serviceProperties';

// Constraints mirror the Smithy model (HelianthusArbiterServiceModel common.smithy).
const RULE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{0,255}$/;
const RESOURCE_TYPE_PATTERN = /^[A-Za-z][A-Za-z0-9]*::[A-Za-z][A-Za-z0-9]*::[A-Za-z0-9:/_-]+$/;
const INPUT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,127}$/;
const EXPRESSION_MAX_LENGTH = 20000;
const RULE_NAME_RULE = 'start with a letter and contain only letters, numbers, hyphens, and underscores (1-256 characters)';

const SEVERITY_OPTIONS: SelectProps.Option[] = [
  { label: 'Critical', value: 'CRITICAL' },
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

const statusOption = (value: RuleStatus): SelectProps.Option => ({ label: ruleStatusLabel(value), value });
const STATUS_OPTIONS = [RuleStatus.DRAFT, RuleStatus.DRY_RUN, RuleStatus.ACTIVE, RuleStatus.INACTIVE].map(statusOption);

// LLM first: it is the default for new rules. A stored language outside this list round-trips unchanged via findOption.
const LANGUAGE_OPTIONS: SelectProps.Option[] = [
  { label: 'LLM', value: ExpressionLanguage.LLM },
  { label: 'Sangheli 1.0', value: ExpressionLanguage.SANGHELI_1_0 },
  { label: 'Zelkova', value: ExpressionLanguage.ZELKOVA },
];

const SECURITY_CONCERN_OPTIONS: SelectProps.Option[] = Object.values(SecurityConcern).map((value) => ({
  label: value,
  value,
}));


export interface RuleFormValues {
  ruleName: string;
  description: string;
  appliesTo: string[];
  severity?: Severity;
  ruleStatus?: RuleStatus;
  securityConcern?: SecurityConcern;
  serviceProperties?: ServiceProperties;
  inputs: RuleInput[];
  evaluation: RuleEvaluation;
}

interface RuleFormProps {
  initialValues?: Partial<RuleFormValues>;
  header: ReactNode;
  /** Rendered between the header and the first section, e.g. provenance alerts on Edit. */
  banner?: ReactNode;
  submitLabel: string;
  /** Throw to report a server-side failure; the message is shown on the form. */
  onSubmit: (values: RuleFormValues) => void | Promise<void>;
  onCancel: () => void;
}

type FieldErrors = Partial<Record<'ruleName' | 'appliesTo' | 'inputs' | 'expression', string>>;

function findOption(options: SelectProps.Option[], value: string | undefined): SelectProps.Option | null {
  if (!value) return null;
  return options.find((o) => o.value === value) ?? { label: value, value };
}

const invalidResourceType = (value: string) =>
  `Invalid resource type "${value}" (expected Namespace::Type::Name, e.g. Code::Java::All)`;

function validateRuleName(name: string): string | undefined {
  if (!name) return 'Rule name is required';
  if (!RULE_NAME_PATTERN.test(name)) return `Rule name must ${RULE_NAME_RULE}`;
  return undefined;
}

function validateInputs(inputs: InputFormState[]): string | undefined {
  for (const [i, input] of inputs.entries()) {
    const prefix = `Input ${i + 1}:`;
    if (!input.name.trim() || !input.type.trim()) return `${prefix} name and type are required`;
    if (!INPUT_NAME_PATTERN.test(input.name.trim())) {
      return `${prefix} name must start with a letter and contain only letters, numbers and underscores (max 128 characters)`;
    }
    const { required, field } = SOURCE_TYPES[input.sourceType];
    if (required && !input.value.trim()) return `${prefix} ${field} is required for ${input.sourceType} sources`;
  }
  return undefined;
}

function validateExpression(expression: string): string | undefined {
  if (!expression.trim()) return 'Expression is required';
  if (expression.length > EXPRESSION_MAX_LENGTH) return `Expression must be at most ${EXPRESSION_MAX_LENGTH.toLocaleString()} characters`;
  return undefined;
}

export function RuleForm({ initialValues = {}, header, banner, submitLabel, onSubmit, onCancel }: RuleFormProps) {
  // Create lets the user pick a name; Edit always has one and shows it in the header instead.
  const nameEditable = !initialValues.ruleName;
  const [ruleName, setRuleName] = useState(initialValues.ruleName ?? '');
  const [description, setDescription] = useState(initialValues.description ?? '');
  const [appliesTo, setAppliesTo] = useState<string[]>(initialValues.appliesTo ?? []);
  const [appliesToDraft, setAppliesToDraft] = useState('');
  const [severity, setSeverity] = useState(findOption(SEVERITY_OPTIONS, initialValues.severity));
  // A stored status outside the editable set (deprecated, error, ...) stays selectable so it round-trips.
  const initialStatus = initialValues.ruleStatus ? statusOption(initialValues.ruleStatus) : null;
  const statusOptions = initialStatus && !STATUS_OPTIONS.some((o) => o.value === initialStatus.value)
    ? [...STATUS_OPTIONS, initialStatus]
    : STATUS_OPTIONS;
  const [ruleStatus, setRuleStatus] = useState(initialStatus);
  const [securityConcern, setSecurityConcern] = useState(findOption(SECURITY_CONCERN_OPTIONS, initialValues.securityConcern));
  const [serviceProperties, setServiceProperties] = useState<ServicePropertyValues>(
    initialValues.serviceProperties ?? {},
  );
  const [inputs, setInputs] = useState<InputFormState[]>((initialValues.inputs ?? []).map(fromRuleInput));
  const [language, setLanguage] = useState<SelectProps.Option>(
    findOption(LANGUAGE_OPTIONS, initialValues.evaluation?.language) ?? LANGUAGE_OPTIONS[0],
  );
  const [expression, setExpression] = useState(initialValues.evaluation?.expression ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Suggest the resource types real rules use: the classifier vocabulary is closed, so a typo never fires.
  // The catalog is fetched only once the field is focused; most edits never touch it.
  const [suggestionsWanted, setSuggestionsWanted] = useState(false);
  const { rules } = useRules({ limit: 100 }, { enabled: suggestionsWanted });
  const appliesToSuggestions = [...new Set(rules.flatMap((r) => r.appliesTo ?? []))].sort().map((value) => ({ value }));

  const setFieldError = (field: keyof FieldErrors, message: string | undefined) =>
    setErrors((prev) => ({ ...prev, [field]: message }));

  const addAppliesTo = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    if (!RESOURCE_TYPE_PATTERN.test(value)) {
      setFieldError('appliesTo', invalidResourceType(value));
      return;
    }
    setFieldError('appliesTo', undefined);
    setAppliesTo((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setAppliesToDraft('');
  };

  const addInput = () => setInputs([...inputs, { name: '', type: '', sourceType: 'discovery', value: '' }]);
  const removeInput = (index: number) => setInputs(inputs.filter((_, i) => i !== index));
  const updateInput = (index: number, patch: Partial<InputFormState>) =>
    setInputs(inputs.map((input, i) => (i === index ? { ...input, ...patch } : input)));

  const handleSubmit = async () => {
    const trimmedName = ruleName.trim();
    const draft = appliesToDraft.trim();
    const nextErrors: FieldErrors = {
      ruleName: nameEditable ? validateRuleName(trimmedName) : undefined,
      appliesTo: draft
        ? (RESOURCE_TYPE_PATTERN.test(draft) ? `Press Enter to add "${draft}" or clear the field` : invalidResourceType(draft))
        : appliesTo.length === 0 ? 'At least one resource type is required' : undefined,
      inputs: validateInputs(inputs),
      expression: validateExpression(expression),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        ruleName: trimmedName,
        description,
        appliesTo,
        severity: severity?.value as Severity | undefined,
        ruleStatus: ruleStatus?.value as RuleStatus | undefined,
        securityConcern: securityConcern?.value as SecurityConcern | undefined,
        serviceProperties: toServiceProperties(serviceProperties),
        inputs: toRuleInputs(inputs),
        evaluation: { language: language.value as ExpressionLanguage, expression },
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : `Failed to ${submitLabel.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPropertyCount = SERVICE_PROPERTY_KEYS.filter((key) => serviceProperties[key]?.length).length;
  const isLlm = language.value === ExpressionLanguage.LLM;
  const expressionRows = Math.min(40, Math.max(10, expression.split('\n').length));

  return (
    <Form
      header={header}
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>{submitLabel}</Button>
        </SpaceBetween>
      }
      errorText={errors.inputs ?? submitError}
    >
      <SpaceBetween size="l">
        {banner}

        <Container header={<Header variant="h2">Basic Information</Header>}>
          <SpaceBetween size="l">
            {nameEditable && (
              <FormField label="Rule Name" description={`Must ${RULE_NAME_RULE}`} errorText={errors.ruleName} constraintText="Required">
                <Input
                  value={ruleName}
                  onChange={({ detail }) => {
                    setRuleName(detail.value);
                    setFieldError('ruleName', validateRuleName(detail.value.trim()));
                  }}
                  placeholder="my-security-rule"
                />
              </FormField>
            )}

            <FormField label="Description" description="A brief description of what this rule validates">
              <Textarea
                value={description}
                onChange={({ detail }) => setDescription(detail.value)}
                placeholder="Enter a description..."
                rows={3}
              />
            </FormField>

            <FormField
              label="Applies To"
              description="Resource types this rule applies to. Pick from types existing rules use, or enter a new one in the form Namespace::Type::Name."
              constraintText="Required. Select a suggestion or press Enter to add a value."
              errorText={errors.appliesTo}
            >
              <SpaceBetween size="xs">
                <Autosuggest
                  value={appliesToDraft}
                  onChange={({ detail }) => setAppliesToDraft(detail.value)}
                  onSelect={({ detail }) => addAppliesTo(detail.value)}
                  onFocus={() => setSuggestionsWanted(true)}
                  options={appliesToSuggestions}
                  enteredTextLabel={(value) => `Use "${value}"`}
                  placeholder="Code::Java::All"
                  ariaLabel="Applies to"
                  empty="No matching resource types"
                />
                {appliesTo.length > 0 && (
                  <TokenGroup
                    items={appliesTo.map((value) => ({ label: value, dismissLabel: `Remove ${value}` }))}
                    onDismiss={({ detail }) => setAppliesTo((prev) => prev.filter((_, i) => i !== detail.itemIndex))}
                  />
                )}
              </SpaceBetween>
            </FormField>

            <ColumnLayout columns={2}>
              <FormField label="Severity" description="Severity level of rule violations">
                <Select selectedOption={severity} onChange={({ detail }) => setSeverity(detail.selectedOption)} options={SEVERITY_OPTIONS} />
              </FormField>
              <FormField label="Status" description="Lifecycle status of the rule">
                <Select selectedOption={ruleStatus} onChange={({ detail }) => setRuleStatus(detail.selectedOption)} options={statusOptions} />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        <Container
          header={
            <Header variant="h2" description="Which services this rule applies to, and the concern it covers">
              Scope
            </Header>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Security Concern" description="Security bar concern this rule covers">
              <Select
                selectedOption={securityConcern}
                onChange={({ detail }) => setSecurityConcern(detail.selectedOption)}
                options={SECURITY_CONCERN_OPTIONS}
                filteringType="auto"
                placeholder="Unspecified"
              />
            </FormField>

            <ExpandableSection
              // headerCounter is container-variant only, so the count goes in the text.
              headerText={`Service properties${selectedPropertyCount ? ` (${selectedPropertyCount})` : ''}`}
              headerDescription="Characteristics a service must have. Leave empty to place no constraint."
              defaultExpanded={selectedPropertyCount > 0}
            >
              <ColumnLayout columns={3}>
                {SERVICE_PROPERTY_KEYS.map((key) => {
                  const field = SERVICE_PROPERTIES[key];
                  const selected = serviceProperties[key] ?? [];
                  return (
                    <FormField key={key} label={field.label}>
                      <Multiselect
                        selectedOptions={selected.map((value) => ({ label: value, value }))}
                        onChange={({ detail }) => setServiceProperties((prev) => ({
                          ...prev,
                          [key]: detail.selectedOptions.map((option) => option.value as string),
                        }))}
                        options={field.values.map((value) => ({ label: value, value }))}
                        placeholder="Any"
                        filteringType="auto"
                        tokenLimit={3}
                      />
                    </FormField>
                  );
                })}
              </ColumnLayout>
            </ExpandableSection>
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2" actions={<Button onClick={addInput}>Add Input</Button>}>Inputs</Header>}>
          <SpaceBetween size="l">
            {inputs.length === 0 ? (
              <Box textAlign="center" color="text-body-secondary">
                No inputs defined. Click "Add Input" to create one.
              </Box>
            ) : (
              inputs.map((input, index) => {
                const source = SOURCE_TYPES[input.sourceType];
                return (
                  <Container
                    key={index}
                    header={
                      <Header variant="h3" actions={<Button onClick={() => removeInput(index)} variant="link">Remove</Button>}>
                        Input {index + 1}
                      </Header>
                    }
                  >
                    <SpaceBetween size="m">
                      <ColumnLayout columns={2}>
                        <FormField label="Name" constraintText="Required. Bound to this name in the expression.">
                          <Input value={input.name} onChange={({ detail }) => updateInput(index, { name: detail.value })} placeholder="code_diff" />
                        </FormField>
                        <FormField label="Type" constraintText="Required">
                          <Input value={input.type} onChange={({ detail }) => updateInput(index, { type: detail.value })} placeholder="string" />
                        </FormField>
                      </ColumnLayout>

                      <FormField label="Source Type" constraintText="Required">
                        <Select
                          selectedOption={findOption(SOURCE_TYPE_OPTIONS, input.sourceType)}
                          onChange={({ detail }) => updateInput(index, { sourceType: detail.selectedOption.value as SourceType, value: '' })}
                          options={SOURCE_TYPE_OPTIONS}
                        />
                      </FormField>

                      <FormField label={source.field} description={source.description} constraintText={source.required ? 'Required' : undefined}>
                        <Input
                          value={input.value}
                          onChange={({ detail }) => updateInput(index, { value: detail.value })}
                          placeholder={source.placeholder}
                        />
                      </FormField>
                    </SpaceBetween>
                  </Container>
                );
              })
            )}
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2">Evaluation</Header>}>
          <SpaceBetween size="l">
            <FormField label="Language" constraintText="Required">
              <Select selectedOption={language} onChange={({ detail }) => setLanguage(detail.selectedOption)} options={LANGUAGE_OPTIONS} />
            </FormField>

            <FormField
              label={isLlm ? 'Prompt' : 'Expression'}
              description={isLlm
                ? 'Instructions given to the model. State the security property, the approach, common patterns, what is not a finding, and when the result is indeterminate.'
                : 'The expression that evaluates the rule'}
              constraintText={`${expression.length.toLocaleString()} / ${EXPRESSION_MAX_LENGTH.toLocaleString()} characters`}
              errorText={errors.expression}
            >
              <Textarea
                value={expression}
                onChange={({ detail }) => {
                  setExpression(detail.value);
                  if (errors.expression) setFieldError('expression', undefined);
                }}
                placeholder={isLlm
                  ? 'You are a security specialist reviewing a code diff for ...'
                  : "input1.value == 'expected' && input2.count > 0"}
                rows={expressionRows}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Form>
  );
}
